import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

import { fmtFixed } from '../../utils/format';
const WithdrawalsManagement = () => {
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchPendingWithdrawals();
  }, []);

  const fetchPendingWithdrawals = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/withdrawals/pending', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPendingWithdrawals(response.data);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      toast.error('Failed to fetch pending withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (withdrawalId, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(`/admin/withdrawals/${withdrawalId}/process`,
        {
          withdrawal_id: withdrawalId,
          status: status,
          admin_notes: adminNotes
        },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      toast.success(`Withdrawal ${status} successfully`);
      setShowModal(false);
      setAdminNotes('');
      fetchPendingWithdrawals();
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      toast.error(error.response?.data?.detail || 'Failed to process withdrawal');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Withdrawals Management</h1>
          <p className="text-gray-400 text-sm">Review and process user withdrawals</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="bg-red-600/20 px-3 sm:px-4 py-2 rounded-lg border border-red-500/30">
            <p className="text-red-400 font-semibold text-sm">{pendingWithdrawals.length} Pending</p>
          </div>
          <button className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-semibold text-sm">
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>
      </div>

      {/* Withdrawals List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
        </div>
      ) : pendingWithdrawals.length === 0 ? (
        <div className="bg-gray-900 rounded-xl p-12 text-center border border-gray-800">
          <AlertCircle className="h-12 w-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No pending withdrawals</p>
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden md:block bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Crypto</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Date</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {pendingWithdrawals.map((withdrawal) => (
                    <tr key={withdrawal.id} className="hover:bg-gray-800/50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-semibold">{withdrawal.user_name || 'N/A'}</p>
                          <p className="text-gray-400 text-sm">{withdrawal.user_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-white font-bold text-lg">{fmtFixed(withdrawal.amount, 2)} USDT</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400">
                          {withdrawal.crypto_type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className={`font-semibold ${
                          withdrawal.user_balance >= withdrawal.amount ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {fmtFixed((withdrawal.user_balance || 0), 2)} USDT
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-400 text-sm">
                          {new Date(withdrawal.created_at).toLocaleString()}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => {
                              setSelectedWithdrawal(withdrawal);
                              setShowModal(true);
                            }}
                            className="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm font-semibold flex items-center space-x-1"
                          >
                            <Eye className="h-4 w-4" />
                            <span>Review</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile View */}
          <div className="md:hidden space-y-3">
            {pendingWithdrawals.map((withdrawal) => (
              <div key={withdrawal.id} className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-white font-bold">{withdrawal.user_name || 'N/A'}</p>
                    <p className="text-gray-400 text-xs">{withdrawal.user_email}</p>
                  </div>
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400">
                    {withdrawal.crypto_type}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div>
                    <p className="text-gray-400 text-xs">Amount</p>
                    <p className="text-white font-bold text-sm">{fmtFixed(withdrawal.amount, 2)} USDT</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Balance</p>
                    <p className={`font-bold text-sm ${
                      withdrawal.user_balance >= withdrawal.amount ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {fmtFixed((withdrawal.user_balance || 0), 2)} USDT
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Date</p>
                    <p className="text-white text-xs">{new Date(withdrawal.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedWithdrawal(withdrawal);
                    setShowModal(true);
                  }}
                  className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm font-semibold flex items-center justify-center space-x-1"
                >
                  <Eye className="h-4 w-4" />
                  <span>Review</span>
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Review Modal */}
      {showModal && selectedWithdrawal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-800">
            <div className="p-4 sm:p-6 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-bold text-white">Review Withdrawal</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* User Info */}
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-white font-bold mb-3 text-sm sm:text-base">User Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Name</p>
                    <p className="text-white font-semibold text-sm sm:text-base">{selectedWithdrawal.user_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Email</p>
                    <p className="text-white font-semibold text-sm sm:text-base break-all">{selectedWithdrawal.user_email}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Current Balance</p>
                    <p className={`font-bold text-base sm:text-lg ${
                      selectedWithdrawal.user_balance >= selectedWithdrawal.amount ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {fmtFixed((selectedWithdrawal.user_balance || 0), 2)} USDT
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Balance After</p>
                    <p className="text-white font-bold text-base sm:text-lg">
                      {fmtFixed(((selectedWithdrawal.user_balance || 0) - selectedWithdrawal.amount), 2)} USDT
                    </p>
                  </div>
                </div>
              </div>

              {/* Withdrawal Details */}
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-white font-bold mb-3 text-sm sm:text-base">Withdrawal Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Amount</p>
                    <p className="text-white font-bold text-lg sm:text-xl">{fmtFixed(selectedWithdrawal.amount, 2)} USDT</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Cryptocurrency</p>
                    <p className="text-purple-400 font-semibold text-sm sm:text-base">{selectedWithdrawal.crypto_type}</p>
                  </div>
                  <div className="col-span-1 sm:col-span-2">
                    <p className="text-gray-400 text-xs sm:text-sm mb-1">Wallet Address</p>
                    <p className="text-white font-mono text-xs break-all">{selectedWithdrawal.wallet_address}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm">Requested</p>
                    <p className="text-white text-sm">{new Date(selectedWithdrawal.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Balance Check */}
              {selectedWithdrawal.user_balance < selectedWithdrawal.amount && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-red-400 mb-1 text-sm">Insufficient Balance</p>
                      <p className="text-xs sm:text-sm text-gray-300">
                        User does not have sufficient balance for this withdrawal.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <label className="text-white font-semibold mb-2 block text-sm sm:text-base">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this withdrawal..."
                  className="w-full px-3 sm:px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 h-20 sm:h-24 text-sm"
                />
              </div>

              {/* Warning */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 sm:p-4">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-yellow-400 mb-1 text-sm">Important</p>
                    <p className="text-xs sm:text-sm text-gray-300">
                      Approving will deduct {fmtFixed(selectedWithdrawal.amount, 2)} USDT from user's balance. After approval, mark as 'Paid' once transaction is sent.
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  onClick={() => handleProcess(selectedWithdrawal.id, 'approved')}
                  disabled={selectedWithdrawal.user_balance < selectedWithdrawal.amount}
                  className="w-full sm:flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Approve</span>
                </button>
                <button
                  onClick={() => handleProcess(selectedWithdrawal.id, 'rejected')}
                  className="w-full sm:flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold flex items-center justify-center space-x-2 text-sm"
                >
                  <XCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Reject</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalsManagement;
